import { useMemo, useState, type FormEvent } from 'react'
import { loadStripe, type Stripe, type Appearance } from '@stripe/stripe-js'
import {
	Elements,
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js'
import { Loader2, Lock, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * Embedded Stripe card capture for the activation flow.
 *
 * The parent creates a SetupIntent (trpc `stripe.createSetupIntent`) and passes
 * the `clientSecret` + `publishableKey` here. We render Stripe Elements inline
 * (no redirect), confirm the SetupIntent, and hand the resulting payment method
 * id back to the parent so it can be set as the billing default.
 */

// Cache the Stripe.js loader per publishable key so we don't re-import on
// every render (loadStripe must be called outside the component lifecycle).
const stripePromiseCache = new Map<string, Promise<Stripe | null>>()
function getStripe(publishableKey: string): Promise<Stripe | null> {
	let promise = stripePromiseCache.get(publishableKey)
	if (!promise) {
		promise = loadStripe(publishableKey)
		stripePromiseCache.set(publishableKey, promise)
	}
	return promise
}

interface EmbeddedCardSetupProps {
	clientSecret: string
	publishableKey: string
	/** Called once the SetupIntent is confirmed, with the payment method id. */
	onConfirmed: (paymentMethodId: string) => void
	/** Whether the parent is still finalizing (e.g. saving default + redirect). */
	isFinalizing?: boolean
	submitLabel?: string
}

export function EmbeddedCardSetup({
	clientSecret,
	publishableKey,
	onConfirmed,
	isFinalizing,
	submitLabel,
}: EmbeddedCardSetupProps) {
	const stripePromise = useMemo(
		() => getStripe(publishableKey),
		[publishableKey],
	)

	const appearance: Appearance = useMemo(
		() => ({
			theme: 'stripe',
			variables: {
				colorPrimary: '#15803d',
				borderRadius: '8px',
				// Stripe Elements render in a cross-origin iframe, so `inherit`
				// resolves to the iframe's default (a serif), not the app font.
				// Pass the app's system sans stack explicitly — every font here
				// is OS-provided, so no web-font loading is needed.
				fontFamily:
					'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				fontSizeBase: '15px',
			},
		}),
		[],
	)

	return (
		<Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
			<CardForm
				onConfirmed={onConfirmed}
				isFinalizing={isFinalizing}
				submitLabel={submitLabel}
			/>
		</Elements>
	)
}

function CardForm({
	onConfirmed,
	isFinalizing,
	submitLabel,
}: Pick<EmbeddedCardSetupProps, 'onConfirmed' | 'isFinalizing' | 'submitLabel'>) {
	const stripe = useStripe()
	const elements = useElements()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [isReady, setIsReady] = useState(false)

	const busy = isSubmitting || isFinalizing

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault()
		if (!stripe || !elements || busy) return

		setIsSubmitting(true)
		setError(null)

		const { error: submitError } = await elements.submit()
		if (submitError) {
			setError(submitError.message ?? 'Please check your card details.')
			setIsSubmitting(false)
			return
		}

		const { error: confirmError, setupIntent } = await stripe.confirmSetup({
			elements,
			redirect: 'if_required',
		})

		if (confirmError) {
			setError(confirmError.message ?? 'We could not verify your card.')
			setIsSubmitting(false)
			return
		}

		const paymentMethodId =
			typeof setupIntent?.payment_method === 'string'
				? setupIntent.payment_method
				: (setupIntent?.payment_method?.id ?? null)

		if (!paymentMethodId) {
			setError('Card setup did not complete. Please try again.')
			setIsSubmitting(false)
			return
		}

		onConfirmed(paymentMethodId)
		// Keep the spinner running; the parent finishes the flow (save + redirect).
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<PaymentElement
				onReady={() => setIsReady(true)}
				options={{ layout: 'tabs' }}
			/>

			{error && (
				<div className="bg-destructive/10 text-destructive flex items-start gap-2 rounded-md p-3 text-sm">
					<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			<Button
				type="submit"
				className="w-full"
				size="lg"
				disabled={!stripe || !isReady || busy}
			>
				{busy ? (
					<span className="flex items-center gap-2">
						<Loader2 className="h-4 w-4 animate-spin" />
						Verifying your card…
					</span>
				) : (
					<span className="flex items-center gap-2">
						<Lock className="h-4 w-4" />
						{submitLabel ?? 'Save card & activate'}
					</span>
				)}
			</Button>

			<p className="text-muted-foreground flex items-center justify-center gap-1.5 text-center text-xs">
				<Lock className="h-3 w-3" />
				Secured by Stripe · $0 verification, no charge today
			</p>
		</form>
	)
}
